import textGuard from "et3-text-guard";
import { isDefinedString, reduce } from "et3-type-guards";
import { memoize } from "lodash";
import { computed, observable } from "mobx";

import { apiCallLogout } from "../../../api/core-api/api-call-logout";
import { apiCallSignIn } from "../../../api/core-api/api-call-sign-in";
import { Ti18nKeys } from "../../../constants/i18n";
import { isValidPassword } from "../../../helpers/et3-data-helper";
import getCoreHistoryStore from "../core-history-store";
import getCoreTimingStore from "../core-timing-store";
import getCorePersonalStore from "../core-personal-store";
import getCoreUIStore from "../core-ui-store";
import getRootStore from "../root-store";

class CoreLoginStore {
  username = observable.box<string>("");
  password = observable.box<string>("");

  errorKey = observable.box<Ti18nKeys>("EMPTY_STRING");
  signInLoading = observable.box<boolean>(false);

  getIsCredentialsExist = computed(() => {
    const definedUserName = isDefinedString(this.username.get());
    const definedPassword = isDefinedString(this.password.get());

    return definedUserName && definedPassword;
  });

  isSignInDisabled = computed(() => {
    return !this.signInLoading.get() && !this.getIsCredentialsExist.get();
  });
  setError = (key: Ti18nKeys) => {
    this.errorKey.set(key);
  };

  setPassword = (pass: string) => {
    this.setError("EMPTY_STRING");
    this.password.set(pass);
  };
  setUserName = (username: string) => {
    this.setError("EMPTY_STRING");
    this.username.set(username);
  };

  setSignInLoading = (isLoading: boolean) => {
    this.signInLoading.set(isLoading);
  };
  signIn = async () => {
    this.setSignInLoading(true);
    this.setError("EMPTY_STRING");
    const username = this.username.get();
    const password = this.password.get();
    const indexOfEt3Email = username.indexOf("@et3arraf.com");
    const cleanedUserName = username.replace("@et3arraf.com", "");
    const isValidUsername = textGuard.isAdminValidUserName({
      userName: cleanedUserName
    });

    if (
      !isValidPassword(password) ||
      !isValidUsername ||
      indexOfEt3Email === -1
    ) {
      this.setError("INVALID_CREDENTIALS");

      this.setSignInLoading(false);
      return;
    }
    const { data, status, code } = await apiCallSignIn({
      username,
      password
    });

    if (status === "OK") {
      const id = reduce(() => data.id, -1);
      const username = reduce(() => data.username, "");
      const fullName = reduce(() => data.fullName, "");
      const role = reduce(() => data.role, "");
      const lastLogin = reduce(() => data.lastLogin, "");
      const adminPermissions = reduce(() => data.adminPermissions, []);
      const needResetPassword = reduce(() => data.needResetPassword, false);
      getCorePersonalStore().setUser({
        id,
        username,
        fullName,
        role,
        lastLogin,
        needResetPassword
      });
      getCorePersonalStore().setUserPermission(adminPermissions);
      const shouldContinue = await getRootStore().startApp();
      if (!shouldContinue) {
        return;
      }
      this.setSignInLoading(false);
      getCoreUIStore().setSideBarVisisble(true);
    } else if (status === "ERROR") {
      const error = reduce(() => code, "INVALID_CREDENTIALS");
      this.setError(error);

      this.setSignInLoading(false);
    }
  };

  signOutHandler = async () => {
    await apiCallLogout();
    await getCoreTimingStore().sendActiveTimeAndResetData();
    getRootStore().killAppTimersAndListenners();
    getCoreUIStore().isProfileDropdownVisible.set(false);
    getCorePersonalStore().user.set({
      id: -1,
      username: "",
      fullName: "",
      role: "",
      lastLogin: "",
      needResetPassword: false
    });
    localStorage.clear();
    getCoreUIStore().isSideBarVisible.set(false);

    getCoreHistoryStore().setPageName("CORE_LOGIN", "CORE_LOGIN");
    getCoreUIStore().setIsLoading("CORE_LOGIN", false);
  };
}
const getCoreLoginStore = memoize(
  () => new CoreLoginStore(),
  () => 1
);
export default getCoreLoginStore;
